import React, { ReactElement, useContext, useState } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, LayoutChangeEvent } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withSpring, interpolate } from 'react-native-reanimated';
import HeimeText from './HeimeText';
import TouchableHeime from './TouchableHeime';
import { Theme, useThemeStyle, WH, WW } from '../_utils';
import { ThemeContext } from '../_utils/themeContext';

interface TabsProps {
    style?: StyleProp<ViewStyle>;
    tabs: { title: string; content: ReactElement }[];
    background?: keyof Theme;
}

const Tabs = ({ style, tabs, background = 'mainBackground' }: TabsProps): ReactElement => {
    const themedStyle = useThemeStyle(styles);
    const { theme } = useContext(ThemeContext);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    // Animation values
    const borderPosition = useSharedValue(0);

    const handleSetActiveTab = (index: number) => {
        setActiveTab(index);

        borderPosition.value = withSpring(index, {
            damping: 15,
            stiffness: 150,
            mass: 0.8,
        });
    };

    const handleContainerLayout = (event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setContainerWidth(width);
    };

    // Animated border style
    const animatedBorderStyle = useAnimatedStyle(() => {
        if (containerWidth === 0) {
            return {};
        }

        const tabWidth = containerWidth / tabs.length;
        const translateX = interpolate(borderPosition.value, [0, tabs.length - 1], [0, (tabs.length - 1) * tabWidth]);

        return {
            transform: [{ translateX }],
            width: tabWidth,
        };
    });

    return (
        <View style={style}>
            <View
                style={[themedStyle.tabsWrapper, { backgroundColor: theme[background] }]}
                onLayout={handleContainerLayout}
            >
                {tabs.map(({ title }, index) => (
                    <TouchableHeime key={title} onPress={() => handleSetActiveTab(index)} style={themedStyle.tab}>
                        <HeimeText
                            style={[themedStyle.tabText, index === activeTab ? themedStyle.activeTabText : undefined]}
                        >
                            {title.toUpperCase()}
                        </HeimeText>
                    </TouchableHeime>
                ))}
                {/* Animated sliding border */}
                <Animated.View
                    style={[
                        themedStyle.slidingBorder,
                        { backgroundColor: theme.secondaryBackground },
                        animatedBorderStyle,
                    ]}
                />
            </View>
            {tabs[activeTab].content}
        </View>
    );
};

const styles = (theme: Theme) =>
    StyleSheet.create({
        tabsWrapper: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            shadowColor: theme.black,
            shadowOffset: { height: 4, width: 0 },
            shadowOpacity: 0.1,
            zIndex: 2,
            position: 'relative',
        },
        tab: {
            flex: 1,
            paddingTop: WH * 0.02,
            paddingBottom: WH * 0.02,
            position: 'relative',
            zIndex: 1,
        },
        tabText: {
            textAlign: 'center',
            color: theme.main,
            letterSpacing: 1,
            fontSize: WW * 0.04,
            fontWeight: 'normal',
        },
        activeTabText: {
            fontWeight: 'bold',
        },
        slidingBorder: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: 2,
            zIndex: 0,
        },
    });

export default Tabs;
